import { analytics, globalTime, isInLineMiniApp } from '@play-co/gcinstant';
import {
  blockUntilHostIsReady,
  GCInstant,
  usePlatformLineGuest,
} from './lib/gcinstant';
import { configureExtensions } from '@play-co/gcinstant/replicantExtensions';
import startApplication, {
  AppView,
} from '@play-co/timestep-core/lib/startApplication';
import device from '@play-co/timestep-core/lib/device';
import StackView from '@play-co/timestep-core/lib/ui/StackView';
import View, { ViewOpts } from '@play-co/timestep-core/lib/ui/View';
import * as loadingGroups from 'src/loadingGroups';
import * as loadMatch2LevelAssets from './loadMatch2LevelAssets';
import { startOptimisticLoading } from './optimisticLoading';
import { petSpeciesAssets } from 'src/loadingGroups';
import ScreenManager from './game/logic/ScreenManager';
import PopupManager from 'src/game/logic/PopupManager';
import {
  hideLoading,
  setScreenSize,
  showLoading,
  showLocale,
} from './state/ui';
import {
  getApplicationScale,
  getScreenBottom,
  getScreenDimensions,
  getScreenLeft,
  getScreenRight,
  getScreenTop,
  getWebIAPCatalog,
  isPlatformGuest,
  retry,
  System,
  waitForAssets,
  waitForVisibilityChange,
} from 'src/lib/utils';
import { AB } from './lib/AB';
import { sendEntryFinalAnalytics } from 'src/lib/analytics';
import { sendLineTagEvent } from 'src/lib/lineTags';
import {
  getBrowserLocale,
  getStrings,
  getUsableLocale,
  loadableLocales,
  loadLocale,
} from 'src/lib/i18n/i18n';
import setupUserProps from './lib/setupUserProps';
import uiConfig from './lib/ui/config';
import { initializeBitmapFonts } from './lib/bitmapFonts';
import { writeLocaleHint } from './lib/i18n/localeHint';
import { PerformanceAnalytics } from './lib/PerformanceAnalytics';
import {
  measureLoadPhase,
  whenGameLoadFinished,
} from './lib/analytics/gameLoadTracking';
import {
  addSentryContext,
  captureGenericError,
  debugBreadcrumb,
} from 'src/lib/sentry';
import StateObserver, { user } from 'src/StateObserver';
import { createEmitter, createPersistentEmitter } from 'src/lib/Emitter';
import PopupMonitor from './game/logic/PopupMonitor';
import {
  sessionSetup,
  sessionSetupAfterEntryFinal,
  sessionSetupBeforeEntryFinal,
} from 'src/lib/onLogin';
import { PauseSensitiveTimeouts } from 'src/game/logic/PauseSensitiveTimeouts';
import type { State } from './replicant/State';
import { duration } from './replicant/utils/duration';
import { initFlexBox } from '@play-co/timestep-core/flexbox';
import * as Pet from './replicant/pet';
import { updatePrivateProfile } from './lib/profile';
import { onboardingFirstEntry } from './game/launch/onboarding.sequence';
import { trackEntryFirstSubscribe } from './game/launch/entryFirstSubscribe.analytics';
import { startLaunchSequence } from './game/launch/LaunchSequence';
import { levelSystem } from './game/level/level.system';
import { getPet, setPet } from './game/pet';
import { EntityManager } from './game/logic/EntityManager';
import { PetController } from './game/pet/PetController';
import { iapActions } from './lib/iap/iap.redux';
import * as Iap from './replicant/iap';
import { onEntryPurchaseAnalytics } from './game/iap/analytics/iap.purchase';
import {
  importProductsCatalogToPlatform,
  isPlatformPaymentsAvailable,
  isWebIapAvailable,
} from './lib/iap/iap.util';
import type { ProfilerView } from '@play-co/timestep-core/ui';
import { devSettings } from './lib/settings';
import { initializeSessionResume } from './lib/sessionResume';
import {
  isPlatformNative,
  consumeSavedPayload,
} from './lib/native/native.util';
import { getRequiredDecorationAssets } from './game/decoration/helpers';
import * as Fate from './replicant/fate';
import { getDisplayedPetState } from './game/pet/pet.util';
import { isFirstEntry } from './lib/platformUtils';
import { isDreamDriftEntry } from './game/dreamDrift/dreamDrift.utils';
import createIntervalEmitter from './lib/createIntervalEmitter';
import { getLaunchScene } from './lib/stateUtils';
import { getStatusAnimationName } from './game/appearance/appearance.config';
import { appendToConversationData } from './game/conversation/conversation.utils';
import { PlayerPetModel } from './game/pet/PlayerPetModel';
import { Locale } from './replicant/ruleset/locale';
import { preloadRewardedVideoAd } from './game/ads/ads.util';
import { getAutoAddInviteFriendId } from './game/friends/addFriendsV2.sequence';
import { updateSocialGraph } from './game/social/social.utils';
import { Feature } from './replicant/analytics';
import { imageLoaderFeatures } from '@play-co/timestep-core/lib/ui/resource/ImageLoaders';
import { supportsWebP as isWebPSupported } from '@play-co/timestep-core/lib/ui/resource/ImageLoaders';
import { isAVIFSupported } from './lib/avif';
import { setDemoFirstEntryInfoToLocalStorage } from './lib/getDemoState';
import { isCrossplayEntry } from './game/crossplay/crossplay.utils';
import { finishCrossplayEntry } from './game/crossplay/crossplay.sequence';
import { throttle } from './game/animation/animation.util';
import { loadKomojuHostedFieldsSDK } from './game/iap/providers/komoju';
import { provisionProduct } from './game/iap/iap.sequence';
import {
  displayWebGLNotSupportedMessage,
  isWebGLSupported,
} from './lib/noWebgl';
import { getBuruFriendsByRecency } from './replicant/friends/friends.getters';
import { getPlayersProfiles } from './game/friends/friends.profiles';
import { initializeIAP } from './game/iap/component/iap.api';
import {
  canDisplayNativeLogin,
  showNativeLogin,
} from './lib/native/native.login';
import { isLoadingFTUEActive } from './lib/ftue';
import { getLineConfig, LineConfig } from './conf/env';
import { miniAppMigrationAfterGameStart } from './lib/miniappMigration';
import { fireYahooAdsPageViewAfterLiffReady } from './lib/yahooAds';

// check to see if the game is running in demo mode
const isDemoSubdomain =
  window.location.hostname.startsWith('demo.') ||
  window.location.pathname.includes('/dev/guest');

export default class Application extends View implements AppView {
  private _isInit = false;
  private rootView!: StackView;
  // View shows performance stats
  private profiler!: ProfilerView;
  private pendingMessageCheck: Promise<void> = Promise.resolve();
  // The locale whose strings and bitmap fonts are currently installed.
  private loadedLocale: Locale | undefined;
  // The pet assets requested before the splash comes down, which the
  // background loading chain continues from.
  private petLoading?: {
    pet: Promise<unknown>;
    statusAnimation: Promise<unknown> | undefined;
  };
  private hasStartedPostSplashWork = false;

  public screenManager!: ScreenManager;
  public popupManager!: PopupManager;
  public systems!: Record<'level', System>;

  constructor(opts: ViewOpts) {
    super(opts);

    // Enable WebP loading, the timestep will check if it supported by client, so we don't need to check it here
    // On local development we do not generating webp images(it slow), so we need to disable it.
    imageLoaderFeatures.enableWebPLoading = !(
      process.env.REPLICANT_OFFLINE && process.env.IS_DEVELOPMENT
    );
    imageLoaderFeatures.enableAvifLoading = !(
      process.env.REPLICANT_OFFLINE && process.env.IS_DEVELOPMENT
    );

    // Enable IAP for the mock platform
    if (process.env.REPLICANT_OFFLINE) {
      importProductsCatalogToPlatform();
    }

    // Run entry point for game
    this.onGameStart()
      .catch(async (error) => {
        // If an error occurs early in the initialization chain, platform stops
        // execution right away which swallows the error logs.
        // Logging before re-throwing gives us visibility into these errors:
        console.error(error);

        // Duplicate error to analytics
        analytics.pushError('AppInitFailed', error);

        // We don't return this promise and this errors newer go up to sentry logger let's log error manually
        captureGenericError('AppInitFailed', error);

        // Force analytics flush to ensure we capture the error
        await analytics.flush();

        return Promise.reject(error);
      })
      .finally(() => {
        this._isInit = true;
      });
  }

  public get isInit() {
    return this._isInit;
  }

  private overrideOfflinePlayerID() {
    if (!process.env.REPLICANT_OFFLINE) {
      return;
    }

    const overriddenID = this.getOverriddenPlayerID();
    if (overriddenID) {
      GCInstant.playerID = overriddenID;
      GCInstant.playerName = 'Player ' + overriddenID;
    }
  }

  private getOverriddenPlayerID(): string | null {
    if (!process.env.REPLICANT_OFFLINE) {
      return null;
    }

    const param = window.location.search
      .replace(/\?/g, '')
      .split('&')
      .find((x) => x.startsWith('profile='));

    return param ? param.substr('profile='.length) : null;
  }

  private createViews() {
    // create rootView
    const scale = getApplicationScale();
    const rootView = new StackView({
      superview: this,
      x: (device.width - uiConfig.width) / 2,
      y: (device.height - uiConfig.height) / 2,
      width: uiConfig.width,
      height: uiConfig.height,
      scale,
      anchorX: uiConfig.width / 2,
      anchorY: uiConfig.height / 2,
      infinite: true,
      canHandleEvents: false,
    });
    this.rootView = rootView;

    // Create screen manager
    this.screenManager = new ScreenManager({ rootView });

    this.screenManager.showScreen('pet');

    // create popup manager
    this.popupManager = new PopupManager({ rootView });

    // resize
    const resize = throttle(() => this.onResize(), 200);
    device.screen.on('Resize', () => resize());
    this.onResize();
  }

  /**
   *  Create initial states for local pet.
   */
  private initializeLocalPet() {
    const { sceneManager } = this.screenManager.screens.pet!;
    const petController = new PetController(
      sceneManager,
      EntityManager.createOrGetPlayerPet(),
    );
    setPet(petController);
    petController.init(new PlayerPetModel());

    const { user } = StateObserver.getState();
    const { hasPermanentStatusEffect } = Fate.getters;
    const now = StateObserver.now();
    const isHidden =
      hasPermanentStatusEffect(user, 'daycare', now) &&
      !hasPermanentStatusEffect(user, 'asleep', now);
    if (isHidden) {
      petController.hide();
    }
  }

  private createSystems() {
    this.systems = {
      level: levelSystem(),
    };

    createPersistentEmitter(({ user }) =>
      Pet.getters.getEquippedPetSpecies(user),
    ).addListener(async (species) => {
      const { pet, ui, user } = StateObserver.getState();

      getPet().appearance.setAttributes({ name: user.pet.name });

      if (
        !species ||
        pet[GCInstant.playerID].action === 'evolve' ||
        ui.isPetCrateOpening
      ) {
        return;
      }

      // Only show loading screen when changing species.
      if (pet[GCInstant.playerID].species) {
        await waitForAssets(petSpeciesAssets[species], {
          feature: Feature.PET._,
          subFeature: Feature.PET.ASSETS,
        });
        appendToConversationData(species);
      }

      getPet().appearance.setSpecies(species);
    });

    // Sync pet status effects once per second
    createIntervalEmitter(
      ({ user, care }, now) => ({
        ...Fate.getters.getAllPermanentStatusEffects(user, now),
        currentCareAction: care.currentCareAction,
      }),
      duration({ seconds: 1 }),
    ).addListener(
      (petState) =>
        !petState.currentCareAction &&
        getPet().appearance.setStatusEffects(petState),
    );

    this.systems.level.start();
  }

  public getRootView() {
    return this.rootView;
  }

  /**
   * Game start callback
   */
  private async onGameStart() {
    // Check for WebP and AVIF image support
    const imageSupportPromise = Promise.all([
      isWebPSupported(),
      isAVIFSupported(),
    ]);

    // Track loading progress counter
    let loadingProgress = 0;

    // Session setup that depends on some contextual platform properties (e.g. demographics)
    // that would be available just before EntryFinal is sent
    GCInstant.beforeEntryFinal(async () => {
      await sessionSetupBeforeEntryFinal();
    });

    // Set storage adapter
    // We use custom storage adapter because createStorageAdapter store data in gcinstant.platformStorage
    // We already storing it in state.platformStorage, migration won't work for this case: https://app.circleci.com/pipelines/github/play-co/buruburu/1113/workflows/6122f89e-8456-4266-a34b-6a19e3300f7e/jobs/5943?invite=true#step-109-7
    GCInstant.storage.setStorageAdapter({
      load: async () => {
        await StateObserver.replicantClientPromise;
        return StateObserver.getState().user.platformStorage;
      },
      save: async (data) => {
        await StateObserver.replicantClientPromise;

        const replicant = StateObserver.replicant;

        const setAnalyticsStoragePromise = !replicant.isPaused()
          ? replicant.setAnalyticsStorage(data)
          : undefined;

        void replicant.invoke.setPlatformStorage(data);

        await Promise.all([
          replicant.flush(), // Flushing is required for accurate analytics

          setAnalyticsStoragePromise,
        ]);
      },
    });

    // Set internal or LINE friends form the state instead of using LINE Game API
    GCInstant.setExternalFriendsGetter(async () => {
      // We need to wait for replicant first
      await StateObserver.replicantClientPromise;
      const { user } = StateObserver.getState();

      // Returns buruFriends (internal friends) from state, newest first so the
      // profile fetch cap drops the oldest friendships rather than arbitrary ones
      const friendsIds = getBuruFriendsByRecency(user);
      const profiles = await getPlayersProfiles(friendsIds);

      return profiles;
    });

    // Init session resume before app init in case the app needs to be reloaded
    const sessionResumeEntryData = isPlatformNative()
      ? await initializeSessionResume()
      : {};

    // Manual initialization of GCInstant
    const isGuestMode = GCInstant.platformID === 'line-guest';
    let lineConfig: LineConfig | undefined;

    if (!isGuestMode) {
      lineConfig = getLineConfig(GCInstant.legacyNative);
    }

    if (process.env.IS_LOCAL_DEVELOPMENT && window.liff) {
      if (window.liff.id) {
        await window.liff.ready;
      } else if (lineConfig?.liffId) {
        await window.liff.init({ liffId: lineConfig.liffId });
      }
    }

    void fireYahooAdsPageViewAfterLiffReady();

    // Engine work that needs nothing from the platform overlaps its init. A
    // login redirect out of `initializeAsync` only costs bandwidth: whatever
    // downloaded is cached for the return.
    const engineReady = this.loadEngine();
    // A rejection surfaces through the `Promise.all` below. The no-op catch
    // only stops one raised during platform init from being reported unhandled.
    void engineReady.catch(() => undefined);

    await measureLoadPhase('platformInit', () =>
      GCInstant.initializeAsync({
        appID: process.env.APP_ID!,
        shortName: process.env.APP_SHORT_NAME!,
        version: process.env.APP_VERSION!,
        // If this flag will be set false for iOS native app v2.12(232+)
        // it will cause redirect on initializeAsync from native app to external browser!!
        enableExperimentalNativeLineLogin: canDisplayNativeLogin(),
        enableLineIDTokenAuthentication: true,
        overrideEnvironmentVariables: isGuestMode,
        lineConversionAPIConfig: {
          channelID: lineConfig?.channelId,
        },
        lineLiffEndpointURL: lineConfig?.miniAppEndpointUrl,
        lineLiffID: lineConfig?.liffId,
        lineLoginChannelID: lineConfig?.channelId,
        lineLoginScopes: lineConfig?.scopes,
        lineMiniAppLiffID:
          lineConfig?.channel === 'miniapp' ? lineConfig.liffId : undefined,
        lineOfficialAccountID: lineConfig?.oaId,
      }),
    );

    // Add sentry metadata
    addSentryContext();

    // Show fake progress
    const loadingInterval = window.setInterval(() => {
      loadingProgress += 1;
      GCInstant.setLoadingProgress(loadingProgress);
    }, 50);

    const visibilityStateBeforeInit = document.visibilityState;

    // Block Replicant initialization until document becomes visible
    if (visibilityStateBeforeInit === 'hidden') {
      await waitForVisibilityChange();
    }

    // Register onPause to flush pending queues
    GCInstant.onPause(this.onPause);
    GCInstant.onResume(this.onResume);

    // Load asset and replicant in parallel
    await Promise.all([
      engineReady,
      measureLoadPhase('replicantReady', () =>
        this.onReplicantReady(visibilityStateBeforeInit),
      ),
    ]);

    // Consume saved payload.
    Object.assign(sessionResumeEntryData, consumeSavedPayload());

    // Create views.
    this.createViews();

    // Load the launch scene and the pet after Replicant is ready. The rest of
    // the background assets wait for the splash, see startPostSplashWork().
    if (!isLoadingFTUEActive()) {
      await measureLoadPhase('firstScene', () => this.startFirstSceneLoading());
      this.startPetLoading();
    }

    // Set analytics user properties
    setupUserProps();

    // Resolve early entry sequence before `startGameAsync`.
    // Resolves immediately if early entry sequence is unavailable.
    await measureLoadPhase('startGame', () =>
      GCInstant.waitForEarlyEntryData(),
    );

    // Ensure friends are loaded before startGameAsync computes connectedPlayerCount
    await measureLoadPhase('friends', () =>
      GCInstant.waitForFriends().catch((error) => {
        captureGenericError('Failed to wait for friends', error);
      }),
    );

    // Make sure progress bar is filled
    const progressBarFill = document.getElementById('progressBarFill');
    if (progressBarFill) {
      progressBarFill.style.animation = 'none';
      progressBarFill.style.width = '100%';
    }

    // Starts the game once assets are loaded and the backend is ready.
    await measureLoadPhase('startGame', () => GCInstant.startGameAsync());

    // Check subFeature after startGameAsync, when the entry data payload is available
    const subFeature = GCInstant.entryData?.['$subFeature'];

    // Update player node in social graph. Runs after `startGameAsync` because the
    // inviter is read from the entry data payload, which is only available then.
    const socialGraph = updateSocialGraph(
      this.rootView,
      getAutoAddInviteFriendId(),
    );

    // If user enters the game from some of the retention notification, revive the pet
    const revivePet =
      subFeature === Feature.NOTIFICATION.RETENTION_D3 ||
      subFeature === Feature.NOTIFICATION.RETENTION_D4 ||
      isDreamDriftEntry(user());
    if (revivePet) {
      await StateObserver.invoke.revivePet();
    }

    // Notify native wrapper (if present) that game has loaded
    GCInstant.legacyNative?.game.show();

    // Augment entry data with cached session resume payload
    Object.assign(GCInstant.entryData, sessionResumeEntryData);

    // Demo mode: Save first entry AD info to localstorage in demo mode
    if (isFirstEntry() && isDemoSubdomain) {
      setDemoFirstEntryInfoToLocalStorage();
    }

    // 2nd after startGameAsync()
    this.overrideOfflinePlayerID();
    addSentryContext();

    // Remove fake loading progress
    window.clearInterval(loadingInterval);

    // Resolves after entry analytics have been sent
    await measureLoadPhase('startGame', () =>
      GCInstant.waitForEntryAnalytics(),
    );

    // Track crossplay login
    if (isCrossplayEntry()) {
      finishCrossplayEntry();
    }

    // Wait for social graph information before sending entry finale
    await measureLoadPhase('entryFinal', () => socialGraph);

    // Record native launch timestamp
    await measureLoadPhase('entryFinal', () => this.initializeNativeTracking());

    // This was called earlier and very likely to be resolved here
    const imageSupport = await measureLoadPhase(
      'entryFinal',
      () => imageSupportPromise,
    );

    await measureLoadPhase('entryFinal', () =>
      sendEntryFinalAnalytics({
        supportsWebP: imageSupport[0], // WebP flag
        supportsAvif: imageSupport[1], // AVIF flag
      }),
    );

    // Traditional consent / pre-existing friendship: the user is already an OA
    // friend when the session starts, so the first-session subscription is
    // confirmed as soon as EntryFinal is out — during the splash screen. The
    // simplified-consent paths are reported from their popups instead
    // (profile.api.ts / chatbot.api.ts). No first-entry check needed: the
    // helper no-ops unless this session's EntryFinal had first=true.
    if (GCInstant.isSubscribedToBot) {
      trackEntryFirstSubscribe('preEntry', 'splash_screen');
    }

    // TEMPORARY: Send TagTestEntryFinal event to LINE Tags for iOS verification.
    // Remove once testing is complete. See #9936.
    sendLineTagEvent('TagTestEntryFinal');

    // Mini app after entry final checks
    await measureLoadPhase('afterEntryFinal', () =>
      miniAppMigrationAfterGameStart(),
    ); // This code can do redirect

    if (Pet.getters.getCurrentEvolution(user())) {
      await measureLoadPhase('afterEntryFinal', () =>
        StateObserver.invoke.schedulePetActivity(),
      );
    }

    // On first entry, show optional profile consent while the LINE loading screen
    // is still up. This runs after EntryFinal so the D1 denominator is unaffected;
    // a consent grant is persisted by the consent flow itself.
    if (isFirstEntry() && !isPlatformGuest(GCInstant) && !isPlatformNative()) {
      try {
        await onboardingFirstEntry('splash_screen');
      } catch (error) {
        // Onboarding must never abort launch: swallow and continue so the
        // launch sequence below still runs.
        captureGenericError('onboardingFirstEntry failed', error);
      }
    }

    // Set the session data on the platform
    GCInstant.setSessionData(StateObserver.getSessionData());

    PopupMonitor.init();

    this.initializeLocalPet();

    this.createSystems();

    // Show profiler view, only for dev
    this.initPerformanceProfiler();

    // Session work that does not have to precede the splash starts once the
    // game reports it is ready to dismiss it.
    void whenGameLoadFinished().then(() => this.startPostSplashWork());

    // Switch to a scene and start opening popups.
    await measureLoadPhase('launchSequence', () => startLaunchSequence());

    // A launch path that never reports load finish still gets its session work.
    this.startPostSplashWork();

    // Initialize performance analytics
    PerformanceAnalytics.init();

    // Update client timezone
    const tempDate = new Date();
    const timezoneOffsetMs: number = duration({
      minutes: tempDate.getTimezoneOffset(),
    });

    StateObserver.invoke.updateUserClientSideData({
      timezoneOffsetMs: timezoneOffsetMs,
    });
  }

  /**
   * The blocking assets, the flexbox engine and the launch warm-up need
   * nothing from the platform, so they run alongside `initializeAsync`. A
   * hidden tab keeps waiting for visibility first, so it stays off the
   * network until it is looked at (#9275).
   */
  private async loadEngine(): Promise<void> {
    if (document.visibilityState === 'hidden') await waitForVisibilityChange();

    // The blocking assets go out first so the warm-up cannot compete with them.
    const assets = measureLoadPhase('initialAssets', () =>
      this.onAssetLoaded(),
    );
    startOptimisticLoading('launch');

    await Promise.all([assets, initFlexBox()]);
  }

  /**
   * Asset loading callback
   */
  private async onAssetLoaded() {
    // Warning: Due to race conditions, state is not guaranteed to be available here!
    const assetsToLoad = [loadingGroups.initialAssets.load()];

    // Loaded assets
    await Promise.all(assetsToLoad);
  }

  /**
   * On replicant ready
   */
  private async onReplicantReady(
    visibilityStateBeforeInit: DocumentVisibilityState,
  ): Promise<void> {
    // Wait for replicant start
    await measureLoadPhase('replicant', () => StateObserver.init());

    // Resolve platform storage before initializing A/B tests.
    // No deoptimization here as platform.loadStorage just reads from Replicant state:
    await GCInstant.loadStorage();

    // Init AB test
    AB.initialize();

    // Various things! Do them once!
    await sessionSetup();

    // Calibrate local time to match server time
    globalTime.setNow(StateObserver.now());

    // Detect and load preferred user locale
    // Also monitor local change
    await this.loadUserLocale();

    // Warm up what the state and locale are enough to name.
    startOptimisticLoading('replicantReady');
  }

  private initializeIap() {
    // Component IAP for LINE Mini app
    if (isInLineMiniApp()) {
      initializeIAP()
        .then((isReady) => {
          // Disables purchases if the catalog is empty
          StateObserver.dispatch(
            iapActions.setIapReady(isReady && GCInstant.catalog.length > 0),
          );
        })
        .catch((e) => {
          StateObserver.dispatch(iapActions.setIapReady(false));
          captureGenericError('IAP init failed', e);
        });
      // Exit early if we are in LINE Mini app
      return;
    }

    if (!isPlatformPaymentsAvailable()) {
      // TODO Uncomment after Apple approval
      // || isPlatformGuest(GCInstant)) {
      return;
    }

    // Initialize in app purchases with retry
    retry(() => GCInstant.iapInit({ productIDs: Iap.ruleset.productIds }), {
      // Retry 2 times on Android app only, otherwise do not retry
      retryCount: GCInstant.osType === 'ANDROID_APP' ? 2 : 0,
    })
      .then(() => {
        if (isWebIapAvailable()) {
          GCInstant.paymentsAvailable = true; // Override IAP availability status in analytics

          const webIAPCatalog = getWebIAPCatalog();
          GCInstant.catalog = webIAPCatalog;

          // Start loading the hosted fields SDK.
          void loadKomojuHostedFieldsSDK();

          return webIAPCatalog;
        }

        return GCInstant.getCatalogAsync();
      })
      .then((catalog) => {
        // Disables purchases if the catalog is empty
        StateObserver.dispatch(iapActions.setIapReady(!!catalog.length));
      })
      .catch((e) => {
        StateObserver.dispatch(iapActions.setIapReady(false));
        captureGenericError('IAP init failed', e);
      });

    // Register onProvisionProductAsync() to handle purchases
    GCInstant.onProvisionProductAsync(provisionProduct);
  }

  private async initializeNativeTracking() {
    // Set up analytic storage
    const { googleLastLaunched, appleLastLaunched } = user().native;

    if (
      googleLastLaunched &&
      GCInstant.storage.entry.lastNativeAndroidEntry === 0
    ) {
      GCInstant.storage.assign((storage) => {
        storage.entry.lastNativeAndroidEntry = googleLastLaunched;
      });
    }

    if (appleLastLaunched && GCInstant.storage.entry.lastNativeIOSEntry === 0) {
      GCInstant.storage.assign((storage) => {
        storage.entry.lastNativeIOSEntry = appleLastLaunched;
      });
    }

    // Save the timestamp on replicant
    const os = GCInstant.legacyNative?.platform.os;
    if (!isPlatformNative() || !os) {
      return;
    }

    switch (os) {
      case 'android': {
        await StateObserver.invoke.updateGoogleLastLaunched();
        break;
      }
      default: {
        await StateObserver.invoke.updateAppleLastLaunched();
        break;
      }
    }
  }

  /**
   * Detect and load user locale
   */
  private async loadUserLocale() {
    // Get locale from settings
    let initialLocale = StateObserver.getLocale();

    // If local not set, detect it based on browser language
    if (!initialLocale || !loadableLocales[initialLocale]) {
      let locale = getBrowserLocale();
      initialLocale = getUsableLocale(locale as Locale);

      // Save locale to replicant
      await StateObserver.invoke.updateSettings({
        locale: initialLocale,
      });
    }

    // Remember the resolved locale so the next launch warms up the right one.
    writeLocaleHint(initialLocale);

    // Load locale fonts
    await Promise.all([
      loadLocale(initialLocale),
      initializeBitmapFonts(initialLocale),
    ]);
    this.loadedLocale = initialLocale;

    // Update UI
    StateObserver.dispatch(showLocale(initialLocale));

    // Monitor the locale switch
    createEmitter(
      this.getRootView(),
      ({ user }) => user.settings.locale,
    ).addListener(
      async (lang) => this.onLocaleChange(lang as Locale),
      { noInit: true }, // Initial locale is loaded and set just above.
    );
  }

  /**
   * Listen for turn on and turn of monitoring triggers
   * https://github.com/play-co/timestep-core/blob/master/src/ui/ProfilerView.ts#L52
   */
  private initPerformanceProfiler() {
    if (devSettings.get('profiler') === true)
      void this.loadPerformanceProfiler();
  }

  /**
   * Dynamically load performance monitoring extension on request
   */
  private async loadPerformanceProfiler() {
    const { ProfilerView, ProfilerGraphType } =
      await import('@play-co/timestep-core/ui.js');

    this.profiler = new ProfilerView({
      superview: this.rootView,
      graph: ProfilerGraphType.FPS,
      scale: 0.6,
    });
  }

  public async startFirstSceneLoading() {
    const initialScene = getLaunchScene();
    const sceneManager = this.screenManager.screens.pet!.sceneManager;

    // The scene's own assets and the room decoration on top of it are separate
    // groups with nothing to say to each other, so they are requested together
    // rather than the decoration waiting on the scene's slowest spritesheet.
    await Promise.all([
      sceneManager.preloadScene(initialScene),
      initialScene === 'livingRoom'
        ? getRequiredDecorationAssets('living').load()
        : undefined,
    ]);
  }

  /**
   * Request the pet's own assets and its status clip. These are the only
   * background assets the game needs right after the splash, so they start
   * before it. Starts once; later calls return the same requests.
   */
  private startPetLoading() {
    if (this.petLoading) return this.petLoading;

    const user = StateObserver.getState().user;

    // Start pet assets loading
    const species = Pet.getters.getEquippedPetSpecies(user);

    // Load pet assets or crate if no species.
    const pet = species
      ? petSpeciesAssets[species].load()
      : loadingGroups.petCrateAssets.load();

    // Figure out what assets we need to load for the current pet status.
    const petStatus = getStatusAnimationName(getDisplayedPetState(user));
    const locale = StateObserver.getLocale();
    const petStatusAnimations =
      locale === 'ja'
        ? loadingGroups.petStatusAnimationsJP
        : loadingGroups.petStatusAnimationsEN;

    const statusAnimation = petStatus
      ? petStatusAnimations[petStatus].load()
      : undefined;

    this.petLoading = { pet, statusAnimation };
    return this.petLoading;
  }

  /**
   * Load everything else the session will need, after the pet. Runs once the
   * splash is down so its downloads and decoding do not compete with launch.
   */
  private startBackgroundLoading() {
    const { pet, statusAnimation } = this.startPetLoading();

    startOptimisticLoading('background');

    void pet
      // Load Match2 level assets.
      .then(() => loadMatch2LevelAssets.loadMatch2LevelAssets())
      // Load the status animation
      .then(() => statusAnimation)
      // Load all background assets.
      .then(() => loadingGroups.getBackgroundAssets().load())
      // Then start loading other scenes so they are ready when we need them.
      .then(() => this.screenManager.screens.pet!.sceneManager.preloadScenes());

    // Warm up what only had to stay out of the pet's way. Kept off the chain
    // above so it starts as soon as the pet is in, whether or not the puzzle
    // is. A rejection is the chain's to surface, so it is ignored here.
    void Promise.all([pet, statusAnimation])
      .then(() => startOptimisticLoading('petLoaded'))
      .catch(() => undefined);
  }

  /**
   * Session work that competes with launch for the main thread and the
   * network but is not needed before the splash comes down. Runs once, after
   * the game reports load finish or, failing that, after the launch sequence.
   *
   * Consumers of the IAP catalog already wait on `isIapReady`, and the
   * rewarded ad preload only has to precede the first ad placement.
   */
  private startPostSplashWork() {
    if (this.hasStartedPostSplashWork) return;
    this.hasStartedPostSplashWork = true;

    this.initializeIap();
    preloadRewardedVideoAd('session-start');
    this.startBackgroundLoading();

    // Bookkeeping that nothing in the launch sequence reads back.
    updatePrivateProfile();
    onEntryPurchaseAnalytics();
    sessionSetupAfterEntryFinal().catch((error) =>
      captureGenericError('sessionSetupAfterEntryFinal failed', error),
    );
  }

  /**
   * On locale switch
   */
  private async onLocaleChange(lang: Locale) {
    const locale = getUsableLocale(lang);

    // The settings emitter is added with `noInit`, which leaves its cached
    // value unset, so its first callback comes from the next state change of
    // any kind and carries the locale that is already installed. Reloading it
    // downloaded the strings again and built every bitmap font a second time.
    if (locale === this.loadedLocale) return;

    writeLocaleHint(locale);
    StateObserver.dispatch(showLoading());

    try {
      await Promise.all([loadLocale(locale), initializeBitmapFonts(locale)]);
      this.loadedLocale = locale;
      StateObserver.dispatch(showLocale(locale));
    } finally {
      StateObserver.dispatch(hideLoading());
    }
  }

  public onResize() {
    // rescale rootView
    const scale = getApplicationScale();
    this.rootView.updateOpts({
      backgroundColor: 'black',
      x: (device.width - uiConfig.width) / 2,
      y: (device.height - uiConfig.height) / 2,
      scale,
    });

    // generate screen data
    const screen = getScreenDimensions();
    const screenSize = {
      width: screen.width,
      height: screen.height,
      top: getScreenTop(),
      bottom: getScreenBottom(),
      left: getScreenLeft(),
      right: getScreenRight(),
    };

    // dispatch screen info to redux
    StateObserver.dispatch(setScreenSize(screenSize));
  }

  public onPause = () => {
    if (!StateObserver.replicant || StateObserver.replicant.isPaused()) {
      return;
    }

    debugBreadcrumb('Pause');

    // Flush Replicant
    debugBreadcrumb('Replicant flush started');
    StateObserver.replicant
      .flush()
      .then(() => debugBreadcrumb('Replicant flush done'))
      .catch((e) => debugBreadcrumb('Replicant flush failed: ' + e.message));

    // Pause Replicant
    StateObserver.replicant.pause();

    PerformanceAnalytics.pause();

    PauseSensitiveTimeouts.pause();

    const sceneManager = this.screenManager?.screens.pet?.sceneManager;
    sceneManager?.pause();
  };

  public onResume = () => {
    if (!StateObserver.replicant?.isPaused()) {
      return;
    }

    debugBreadcrumb('Resume');

    StateObserver.replicant.resume();

    PerformanceAnalytics.resume();

    PauseSensitiveTimeouts.resume();

    const sceneManager = this.screenManager?.screens.pet?.sceneManager;
    sceneManager?.resume();
  };
}

/**
 * Main entry point for the application.
 *
 * @returns {Promise<void>} - Resolves when the application is ready to start
 */
async function main() {
  if (!isWebGLSupported()) {
    displayWebGLNotSupportedMessage();
    return;
  }

  if (process.env.IS_DEVELOPMENT) {
    const { setupTextureInspector } = await import('./lib/textureInspector.js');
    setupTextureInspector();
  }

  await blockUntilHostIsReady();

  // Handle an issue in the Android native app (native-builder) where the `window.TLNative` property
  // injected by the native host is not always available by the time client code gets evaluated:
  const isInAndroidApp = !!window.ReactNativeWebView;
  const bridgeCodeIsInjected = !!(window.Native || window.TLNative);

  if (isInAndroidApp && !bridgeCodeIsInjected) {
    location.reload();
    return;
  }

  // Show game build in native login screen
  // Works only 2.12(232+) on for iOS native app
  if (canDisplayNativeLogin()) {
    try {
      // What login we should use
      const loginResult = await showNativeLogin({});

      // If user chose one of guest logins, we set the flag
      // If something else is returned, we assume that the user logged in with LINE
      if (loginResult === 'apple_login' || loginResult === 'guest_login') {
        // Force to switch GCInstant to web platform mode
        usePlatformLineGuest();
      }
    } catch (error) {
      // The game cannot be started if the native login crashes
      console.error('Native login failed:', error);
      captureGenericError('Native login failed', error);
      return;
    }
  }

  // https://docs.dev.gc-internal.net/gcinstant/replicantExtensions/#usage
  configureExtensions({
    analytics,
    gcinstant: GCInstant,
    replicantClientPromise: StateObserver.replicantClientPromise,
    getPlatformStorage: (state: State) => state.platformStorage,
  });

  // Start the application
  const instance = startApplication(Application);

  // Expose singleton Application to window in development mode.
  if (process.env.IS_DEVELOPMENT) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).Application = instance;
  }
}

// Start the application
void main();
